import * as React from 'react';
import { Dispatch } from 'react-redux';
import { Draggable, DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';

import { Wrapper, Inner, SelectedNumber, IconEdit, IconCheck, IconReference } from '../../Item/styles';
import ImageWrapper from '../../Item/ImageWrapper';
import ItemInfo from '../../Item/ItemInfo';
import { ThumbnailData, ThumbnailType } from '../../constants';
import { changePageSpread, selectPageSpread, setPagesSelection, resetPagesSelection } from '../../../../../containers/Editor/actions';
import { StoreState } from '../../../../../reducers';
import Utils from '../../../../../utils';

interface DraggableItemProps {
	spreadIndex: number;
	thumbnailsData: ThumbnailData[];
	isActive: boolean;
	isEditable: boolean;
	isPagesSelectionActive: boolean;
	activeDraggingPosition: number | null;
	isAllPagesSelected: boolean;
	isGapInSelectedPages: boolean;
	isPagesSelectionAvailable: boolean;
	selectedPagesNumber?: number;
	dispatch: Dispatch<StoreState>;
	isAccessibleVersion: boolean;
}

const DraggableItem = ({
	spreadIndex,
	thumbnailsData,
	isActive,
	isEditable,
	isPagesSelectionActive,
	activeDraggingPosition,
	isAllPagesSelected,
	isGapInSelectedPages,
	isPagesSelectionAvailable,
	selectedPagesNumber,
	dispatch,
	isAccessibleVersion,
}: DraggableItemProps) => {
	const thumbnailElement = React.useRef<HTMLDivElement>(null);

	const handleClick = (e: MouseEvent | TouchEvent) => {
		const isPagesSelectionMode = isPagesSelectionAvailable && (e.ctrlKey || e.metaKey || e.shiftKey);

		if (isPagesSelectionMode) {
			if (!isPagesSelectionActive) {
				dispatch(setPagesSelection());
			}
			dispatch(selectPageSpread(spreadIndex, e.ctrlKey || e.metaKey, e.shiftKey));
		} else {
			if (isPagesSelectionActive) {
				dispatch(resetPagesSelection());
				dispatch(changePageSpread(spreadIndex));
			} else if (!isActive) {
				dispatch(changePageSpread(spreadIndex));
			}
		}
	};

	React.useEffect(() => {
		// is needed for automatic scrolling to active thumbnail after pages manipulation (move/remove/duplicate)
		if (isActive && thumbnailElement.current) {
			thumbnailElement.current.focus();
		}
	}, [isActive]);

	const isDragDisabled = isAccessibleVersion || isPagesSelectionActive && (
		!isActive ||
		isGapInSelectedPages ||
		isAllPagesSelected
	);
	const isBlurred = isPagesSelectionActive &&
		isActive &&
		activeDraggingPosition &&
		spreadIndex !== activeDraggingPosition;

	return (
		<Draggable
			draggableId={spreadIndex.toString()}
			index={spreadIndex}
			isDragDisabled={isDragDisabled}
		>
			{(provided: DraggableProvided, { isDragging }: DraggableStateSnapshot) => (
				<Wrapper
					ref={provided.innerRef}
					{...provided.draggableProps}
					{...provided.dragHandleProps}
					type={ThumbnailType.COLUMN}
					isActive={isActive}
					isBlurred={isBlurred}
					onClick={handleClick}
				>
					<Inner
						ref={thumbnailElement}
						tabIndex={0}
					>
						<ImageWrapper thumbnailsData={thumbnailsData} />
						<ItemInfo
							spreadIndex={spreadIndex}
							label={thumbnailsData[0].label}
						/>
						{isEditable && !isPagesSelectionActive &&
							<IconEdit>
								{Utils.getSvgIcon('iconset_uprez_edit')}
							</IconEdit>
						}
						{thumbnailsData[0].extPage && !isPagesSelectionActive &&
							<IconReference>
								{Utils.getSvgIcon('iconset_uprez_reference')}
							</IconReference>
						}
						{isPagesSelectionActive && !isDragging &&
							<IconCheck isChecked={isActive} />
						}
						{isPagesSelectionActive && selectedPagesNumber && selectedPagesNumber > 1 && isDragging &&
							<SelectedNumber>
								{selectedPagesNumber}
							</SelectedNumber>
						}
					</Inner>
				</Wrapper>
			)}
		</Draggable>
	);
};

export default React.memo(DraggableItem);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/Thumbnails/Column/DraggableItem/index.tsx